import React, { useMemo, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

// Component for displaying iframe-based AI tools (Chatbase, Super Interface, etc.)
const ChatBase = () => {
  const { toolName } = useParams();
  const history = useHistory();
  
  // Get the tool data including name and chatbase URL
  const { toolTitle, iframeUrl } = useMemo(() => {
    const defaultUrl = "https://www.chatbase.co/chatbot-iframe/pNsb-ekwEwfKxcAE2Hn-l";
    let title = "AI Assistant";
    
    if (!toolName) return { toolTitle: title, iframeUrl: defaultUrl };
    
    // Get tools from sessionStorage
    const storedTools = sessionStorage.getItem('aiTools');
    if (!storedTools) return { toolTitle: title, iframeUrl: defaultUrl };
    
    try {
      const tools = JSON.parse(storedTools);
      const tool = tools.find(t => t.slug === toolName);
      
      // Return the tool data if found
      if (tool) {
        // Check if URL is a valid iframe tool (Chatbase or Super Interface)
        const isValidIframeUrl = tool.chatbaseUrl && (
          tool.chatbaseUrl.includes('chatbase.co') || 
          tool.chatbaseUrl.includes('superinterface.ai')
        );
        
        return {
          toolTitle: tool.title,
          iframeUrl: isValidIframeUrl ? tool.chatbaseUrl : defaultUrl
        };
      }
    } catch (e) {
      console.error('Error parsing tools from storage:', e);
    }
    
    return { toolTitle: title, iframeUrl: defaultUrl };
  }, [toolName]);

  // Track AI tool usage when component loads
  useEffect(() => {
    if (window?.datafast) {
      if (toolName) {
        // Handle specific tool with toolName parameter
        const storedTools = sessionStorage.getItem('aiTools');
        if (storedTools) {
          try {
            const tools = JSON.parse(storedTools);
            const tool = tools.find(t => t.slug === toolName);
            
            if (tool && tool.title) {
              // Create a clean goal name from the actual tool title
              const cleanToolName = tool.title
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, '') // Remove special characters except spaces
                .replace(/\s+/g, '_') // Replace spaces with underscores
                .substring(0, 25); // Allow more characters for better tool identification
              
              // Track general AI tool usage
              window.datafast('ai_tool_used');
              
              // Track specific tool usage with actual tool name
              window.datafast(`ai_tool_${cleanToolName}`);
            }
          } catch (e) {
            console.error('Error parsing tools for analytics:', e);
            // Fallback to slug-based tracking
            const cleanToolName = toolName
              .toLowerCase()
              .replace(/[^a-z0-9_]/g, '')
              .substring(0, 20);
            
            window.datafast('ai_tool_used');
            window.datafast(`ai_tool_${cleanToolName}`);
          }
        }
      } else {
        // Handle default AI Assistant case (no toolName parameter)
        window.datafast('ai_tool_used');
        window.datafast('ai_tool_default_assistant');
      }
    }
  }, [toolName]);

  const handleBackClick = () => {
    history.push('/ai-toolkit');
  };
  
  return (
    <div className="flex flex-col min-h-full py-6">
      <div className="container mb-3">
        {/* Header with breadcrumbs and back button */}
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            {/* Breadcrumbs */}
            <nav className="flex items-center text-sm text-gray-500">
              <button
                onClick={handleBackClick}
                className="hover:text-gray-700 transition-colors"
              >
                AI Toolkit
              </button>
              <svg
                className="mx-2 h-4 w-4 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-gray-900">{toolTitle}</span>
            </nav>
            
            {/* Back button */}
            <button
              onClick={handleBackClick}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary transition-colors"
            >
              <svg
                className="mr-1.5 h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to AI Toolkit
            </button>
          </div>
          
          {/* Tool title */}
          <h1 className="text-2xl font-bold text-gray-900">
            {toolTitle}
          </h1>
        </div>
      </div>
      
      {/* Iframe container */}
      <div className="container flex-grow overflow-y-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 200px)', minHeight: '400px' }}>
          <iframe
            title="AI Tool"
            src={iframeUrl}
            width="100%"
            height="100%"
            className="ai-tool-iframe"
            style={{ minHeight: '400px' }}
          />
        </div>
      </div>
    </div>
  );
}

export default ChatBase
